import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import {
    UserSettingsApi,
    sanitizeIpSearchOnboardingSettings,
    type IpSearchOnboardingSettingsV1,
    type UserSettingsApi as UserSettingsApiType,
} from '@telecom/shared/domain/user-settings';
import type {
    OnboardingStateService,
    OnboardingStateUpdateMode,
} from '../onboarding-state-service';

interface AccountState {
    state?: IpSearchOnboardingSettingsV1;
    loadPromise?: Promise<IpSearchOnboardingSettingsV1>;
    failure?: unknown;
    updateQueue: Promise<void>;
}

const RESET_ONBOARDING_STATE: IpSearchOnboardingSettingsV1 = {
    entry_status: 'eligible',
    report_eligibility_status: 'eligible',
};

/** 通过用户设置 API 持久化 onboarding 状态，并隔离账号级缓存和写入队列。 */
@injectable()
export class OnboardingStateServiceImpl implements OnboardingStateService {
    private readonly _accounts = new Map<string, AccountState>();

    constructor(
        @inject(UserSettingsApi)
        private readonly _userSettingsApi: UserSettingsApiType,
    ) {}

    async getState(accountId: string): Promise<IpSearchOnboardingSettingsV1> {
        const account = this._getAccount(accountId);
        if (account.failure) throw account.failure;
        if (account.state) return account.state;
        if (account.loadPromise) return account.loadPromise;

        account.loadPromise = this._userSettingsApi.getUserSettings()
            .then((response) => {
                const state = sanitizeIpSearchOnboardingSettings(
                    response.settings?.ip_search_onboarding_v1,
                ) ?? {};
                account.state = { ...state };
                return account.state;
            })
            .catch((error) => {
                account.failure = error;
                throw error;
            })
            .finally(() => {
                account.loadPromise = undefined;
            });
        return account.loadPromise;
    }

    async updateState(
        accountId: string,
        patch: Partial<IpSearchOnboardingSettingsV1>,
        mode: OnboardingStateUpdateMode,
    ): Promise<void> {
        const account = this._getAccount(accountId);
        if (mode === 'optimistic' && account.state) {
            account.state = { ...account.state, ...patch };
        }
        const task = account.updateQueue
            .catch(() => undefined)
            .then(async() => {
                const current = await this.getState(accountId);
                const next = { ...current, ...patch };
                if (mode === 'optimistic') account.state = next;

                try {
                    await this._userSettingsApi.updateUserSettings({
                        settings: { ip_search_onboarding_v1: next },
                    });
                    account.state = next;
                } catch (error) {
                    if (mode === 'required') {
                        account.failure = error;
                        account.state = undefined;
                    }
                    throw error;
                }
            });
        account.updateQueue = task;
        return task;
    }

    async resetState(accountId: string): Promise<void> {
        const account = this._getAccount(accountId);
        const pendingLoad = account.loadPromise;
        const task = account.updateQueue
            .catch(() => undefined)
            .then(async() => {
                await pendingLoad?.catch(() => undefined);
                await this._userSettingsApi.updateUserSettings({
                    settings: {
                        ip_search_onboarding_v1: { ...RESET_ONBOARDING_STATE },
                    },
                });
                account.failure = undefined;
                account.loadPromise = undefined;
                account.state = { ...RESET_ONBOARDING_STATE };
            });
        account.updateQueue = task;
        return task;
    }

    private _getAccount(accountId: string): AccountState {
        let account = this._accounts.get(accountId);
        if (!account) {
            account = { updateQueue: Promise.resolve() };
            this._accounts.set(accountId, account);
        }
        return account;
    }
}
